import { computed, action, isArrayLike, observable, runInAction, toJS } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IDynamicRowDocuments, ITableConfiguration } from '../models';
import { ITableProps } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableProps';
import { IPerformanceRow } from '../models/ITablePerformanceRow';
import { ITableColumn } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableColumn';
import { IBreakpointProps } from '@kurtosys/ksys-app-components/dist/models/IBreakpointProps';
import { ITableComponentProps } from '../models/ITableComponentProps';
import { ITableHeaderGroup } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableHeaderGroup';
import { Table as UnstyledTable } from '@kurtosys/ksys-app-components/dist/components/base/Table/Table';
import { SortButtonDirection } from '@kurtosys/ksys-app-components/dist/components/base/SortButton/models';
import { ResponsiveConfig } from '../../shared/ResponsiveConfig';
import { IDocumentContextMenuData } from '@kurtosys/ksys-app-components/dist/components/base/Table/components/DocumentContextMenu/models/IDocumentContextMenuData';
import { IDocumentContextMenuItemQueries } from '@kurtosys/ksys-app-components/dist/components/base/Table/components/DocumentContextMenu/models/IDocumentContextMenuItemQueries';
import { IDocumentContextMenuItem } from '@kurtosys/ksys-app-components/dist/components/base/Table/components/DocumentContextMenu/models/IDocumentContextMenuItem';
import { utils, models, helpers } from '@kurtosys/ksys-app-template';
import { TableColumnDisplayType } from '@kurtosys/ksys-app-components/dist/components/base/Table/models';
import { IDocumentContextMenuProps } from '@kurtosys/ksys-app-components/dist/components/base/Table/components/DocumentContextMenu/models/IDocumentContextMenuProps';

export class TableStore extends StoreBase {
	static componentKey: 'table' = 'table';
	existingDocumentsByClientCode: Set<string> = new Set<string>();
	// Store the child rows once the store gets initialized, this is to allow the no data to check the child rows
	@observable
	childRows: IPerformanceRow[] = [];

	@observable
	contextedTimestamp: number | undefined;

	@computed
	get configuration(): ITableConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(TableStore.componentKey);
		}
	}

	@computed
	get props(): ITableComponentProps | undefined {
		const activeProps = this.orchestratedProps;
		return activeProps;
	}

	@computed
	get breakpoints(): IBreakpointProps<ITableConfiguration>[] | undefined {
		return this.configuration && this.configuration.breakpoints;
	}

	@computed
	get configurationWithoutBreakpoints(): ITableConfiguration | undefined {
		if (this.configuration) {
			const { breakpoints, ...config } = this.configuration;
			return config;
		}
	}

	@computed
	get activeConfiguration(): ITableConfiguration {
		const responsive = new ResponsiveConfig<ITableConfiguration>(this.storeContext, this.configurationWithoutBreakpoints, this.breakpoints);
		return responsive.activeConfiguration;
	}

	isBreakpointProps(props: ITableComponentProps | IBreakpointProps<ITableComponentProps>[] | undefined): props is IBreakpointProps<ITableComponentProps>[] {
		return !!(props && Array.isArray(props) && (props[0] as IBreakpointProps<ITableComponentProps>).modifier && (props[0] as IBreakpointProps<ITableComponentProps>).size);
	}

	isMultiTableDataSet(data: { [property: string]: IPerformanceRow[]; }): data is { [property: string]: IPerformanceRow[]; } {
		return typeof data === 'object' && Object.keys(data).every((key: string) => data[key].length > 0);
	}

	rowDocuments: { [key: string]: IDocumentContextMenuItem[] } = {};

	orchestrateDocuments = (rows: any[], props: IDocumentContextMenuProps) => {
		const { data, itemTemplate } = props;
		const documentsObject = rows.reduce((response, row) => {
			const rowKey = row._rowKey;
			const rowDocuments = this.getRowDocuments(row, data, itemTemplate);
			// Check if the documents has changed for the row, then regenerate the key and assign the documents.
			// The document context menu component in the table component will pick up that the key changed and will
			// invoke the call back to get the documents
			const currentRowDocuments = this.rowDocuments[rowKey];
			if (!currentRowDocuments || currentRowDocuments.length < 1 || currentRowDocuments.length !== rowDocuments.length) {
				// Clean up the rowDocuments
				delete this.rowDocuments[rowKey];
			}
			row._rowKey = utils.guid.generateGuid();
			response[row._rowKey] = rowDocuments;

			return response;
		}, {});
		this.rowDocuments = { ...this.rowDocuments, ...documentsObject };
	};

	getDocumentsCallback = (rowData: Record<string, any>): IDocumentContextMenuItem[] => {
		return this.rowDocuments[rowData._rowKey] || [];
	};

	private getRowDocuments = (
		rowData: Record<string, any>,
		dataConfig: IDocumentContextMenuData,
		itemConfig: IDocumentContextMenuItemQueries,
	): IDocumentContextMenuItem[] => {
		let documents = this.getQueryValue(dataConfig.source, rowData);
		if (isArrayLike(documents)) {
			if (dataConfig.conditional) {
				const conditional = new helpers.ConditionalHelper({ conditions: [] });
				documents = documents.filter((document) => {
					return conditional.matchesWithOptions({
						instance: document,
						executionOptions: {
							responseContext: rowData,
						},
						conditional: dataConfig.conditional,
					});
				});
			}
			const contextMenuItems: IDocumentContextMenuItem[] = documents.map((document: any) => {
				const { linkTarget, ...queries } = itemConfig;
				return {
					...(this.mergeQueriesAndProps({ queries }, document) as object),
					linkTarget,
				};
			});
			return contextMenuItems;
		}

		return [];
	};

	@computed
	get orchestratedProps(): ITableProps<IPerformanceRow>[] {
		if (this.contextedTimestamp === undefined || !this.storeContext.appStore || !this.storeContext.queryStore) {
			return [];
		}

		const config = this.activeConfiguration;
		let props: ITableProps<IPerformanceRow> = { queryStore: this.storeContext.queryStore, ...this.mergeQueriesAndProps(config) };
		props.sort = { mode: 'single' };
		let multiTableDataSet: ITableProps<IPerformanceRow>[] = [];

		// Get dynamic values
		if (config) {
			const { dynamicRows, dynamicColumns } = config;

			// First Sort out the columns, then it will be available if multi dataset
			if (dynamicColumns && dynamicColumns.query) {
				const { type = 'append', query } = dynamicColumns;
				const columns = this.getQueryValue(query) as ITableColumn[];
				if (type === 'append') {
					props.columns = [...(props.columns || []), ...columns];
				}
				if (type === 'replace') {
					props.columns = columns;
				}
			}

			if (dynamicRows) {
				const rows = this.dynamicRows;
				if (isArrayLike(rows)) {
					props.rows = rows;
				}
				if (this.isMultiTableDataSet(rows)) {
					const keys = Object.keys(rows);
					for (const key of keys) {
						const newProps = { ...props };
						newProps.rows = rows[key];
						// Resolve the table header values based on config key
						const columns = newProps.columns;
						const headerGroups = newProps.headerGroups;
						if (columns && headerGroups) {
							const headerColumnConfig = columns.find(col => !!(col.headerGroupId));
							if (headerColumnConfig) {
								const headerGroupConfig: ITableHeaderGroup[] = headerGroups.map((group) => {
									if (group.id === headerColumnConfig.headerGroupId) {
										group = { ...group, header: key };
									}
									return group;
								});
								newProps.headerGroups = headerGroupConfig;
							}
						}
						multiTableDataSet.push({ ...newProps });
					}
				}
			}

			props.columns = (props.columns || []).map((column) => {
				if (
					column?.display?.type === TableColumnDisplayType.documentContextMenu &&
					column.display.options?.documentContextMenu
				) {
					this.orchestrateDocuments(props?.rows || [], column.display.options?.documentContextMenu);
					return {
						...column,
						display: {
							...column.display,
							options: {
								documentContextMenu: {
									...column.display.options.documentContextMenu,
									getDocumentsCallback: this.getDocumentsCallback,
								},
							},
						},
					};
				}
				return column;
			});

			// the following logic is used to hide rows that don't have data, regardless if it comes from rows or dynamicRows config.
			const { appStore, paginationStore } = this.storeContext;
			if (appStore && appStore.appComponentConfiguration && appStore.appComponentConfiguration.noDataOptions) {
				const { hideRow = false } = appStore.appComponentConfiguration.noDataOptions;
				if (hideRow) {
					if (!utils.typeChecks.isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							return this.noDataCheck(data);
						});
					}
					else {
						props = this.noDataCheck(props);
					}
				}
			}
			if (paginationStore.enabled && !paginationStore.pagingOptions?.isApiBased) {
				const { recordIndex: start, pageSize: limit } = paginationStore;
				if (!utils.typeChecks.isNullOrEmpty(multiTableDataSet)) {
					multiTableDataSet = multiTableDataSet.map((data) => {
						data.rows = (data.rows || []).slice(start, start + limit);
						return data;
					});
				}
				else {
					props.rows = (props.rows || []).slice(start, start + limit);
				}
			}
			if (appStore && appStore.splitAppOptions) {
				const { mustLimitRows, limit, start, hideDisclaimer } = appStore.splitAppOptions;
				if (mustLimitRows) {
					if (!utils.typeChecks.isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							data.rows = (data.rows || []).slice(start, start + limit);
							return data;
						});
					}
					else {
						props.rows = (props.rows || []).slice(start, start + limit);
					}
				}
				if (hideDisclaimer) {
					if (!utils.typeChecks.isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							data.disclaimersProps = undefined;
							return data;
						});
					}
					else {
						props.disclaimersProps = undefined;
					}
				}
			}
			if (this.sort && props.rows) {
				const { column, direction } = this.sort;
				props.rows = this.sortByValue(props.rows, row => (row as any)[column], direction === SortButtonDirection.ascending ? 'ASC' : 'DESC');
			}
			if (props.columns) {
				props.columns.forEach((column) => {
					if (column.sort && column.sort.enabled) {
						column.sort = {
							onChange: this.sortOnChange,
							enabled: true,
						};
					}
				});
			}
		}
		const workingProps = multiTableDataSet.length > 0 ? multiTableDataSet : [props];

		if (workingProps && isArrayLike(workingProps) && workingProps.length === 1) {
			const appKey = this.storeContext && this.storeContext.appStore && this.storeContext.appStore.appKey;
			UnstyledTable.loadSuperscripts(appKey, workingProps[0].columns || [], workingProps[0].rows || [], workingProps[0].headerGroups || [], this.storeContext.queryStore);
		}
		return workingProps;
	}

	sortByValue<T>(
		collection: T[],
		getValueFn: (item: T) => any,
		order: 'ASC' | 'DESC',
	) {
		if (order === void 0) {
			order = 'ASC';
		}
		const result = [...(collection || [])].sort((a, b) => {
			const aValue = getValueFn(a);
			const bValue = getValueFn(b);
			let response = 0;
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				response = bValue > aValue ? -1 : 1;
			}
			if (order === 'DESC') {
				response = response * -1;
			}
			return response;
		});
		return result;
	}

	@observable
	sort: { column: string; direction: SortButtonDirection } | undefined;

	sortOnChange = async (payload: any, direction: SortButtonDirection) => {
		if (payload.direction === 'unset') {
			this.sort = undefined;
		}
		else {
			this.sort = { direction, column: payload.key };
		}
	}

	noDataCheck(props: ITableProps<IPerformanceRow>) {
		const { rows, columns } = props;
		if (rows !== undefined && isArrayLike(rows) && columns !== undefined) {
			props.rows = rows.filter((row: IPerformanceRow) => {
				return !this.rowHasNoData(row, { columnConfig: columns });
			});
		}
		return props;
	}

	@computed
	get hasData(): boolean | undefined {
		const props = this.props;
		if (props) {
			// Ensure that the table checks all rows including the child rows for data
			if (isArrayLike(props)) {
				return (props as ITableProps<IPerformanceRow>[]).some((prop) => {
					const rows = this.getRows(prop);
					return this.rowsHasData(rows, prop.columns);
				});
			}
			const rows = this.getRows(props);
			return props && this.rowsHasData(rows, props.columns);
		}
		return false;
	}

	getRows = (props: ITableProps<IPerformanceRow>) => {
		// Check if the table is going to render child rows, then return them to the has data check
		// Else only the parent grouping rows will be checked
		if (props.grouping && props.grouping.getRowsQuery) {
			return toJS(this.childRows);
		}
		return props.rows || [];
	}

	rowsHasData(rows: IPerformanceRow[] | undefined, columns: ITableColumn[] | undefined): boolean {
		if (Array.isArray(rows) && rows.length > 0) {
			const columnConfig = columns;
			if (columnConfig) {
				const hasNoData = rows.map((row) => {
					return this.rowHasNoData(row, { columnConfig });
				});
				return !hasNoData.every(row => row);
			}
		}
		return false;
	}

	rowHasNoData(row: IPerformanceRow, options: { columnConfig: ITableColumn[]; }): boolean {
		return Object.keys(row).every((columnKey) => {
			const column = options.columnConfig.find(col => col.key === columnKey);
			return !column || column.excludeFromNoDataCheck || row[columnKey] === undefined || row[columnKey] === null;
		});
	}

	async fetchDocuments(rows: any) {
		const fetchAndQueryStore = this.storeContext.fetchAndQueryStore;

		const dynamicRowDocuments = this.dynamicRowDocuments;

		if (dynamicRowDocuments && rows && this.hasData) {
			const { columnKey, documentMetaKey, documentSearchRequest, documentPropertyKey } = dynamicRowDocuments;
			const searchRows = [...rows, ...this.childRows];

			const searchValues = isArrayLike(searchRows)
				? searchRows.filter((row) => row[columnKey]).map((row) => row[columnKey])
				: [];

			if (searchValues.length > 0) {
				let search: models.api.document.IDocumentSearchClause[] = [];

				if (documentPropertyKey) {
					search = [
						{
							matchtype: 'MATCH',
							values: searchValues,
							property: documentPropertyKey,
							meta: false,
						},
					];
				}

				if (documentMetaKey) {
					search = [
						{
							matchtype: 'MATCH',
							values: searchValues,
							property: documentMetaKey,
						},
					];
				}

				if (documentSearchRequest.search.length > 0) {
					search = search.concat(documentSearchRequest.search);
				}

				if (search.length > 0) {
					const body = {
						...documentSearchRequest,
						search,
					};

					await fetchAndQueryStore.documents({
						requestBody: body,
						existingDocumentsByClientCode: this.existingDocumentsByClientCode,
					});
				}
			}
		}
	}

	@computed
	get dynamicRowDocuments(): IDynamicRowDocuments | undefined {
		const config = this.activeConfiguration;
		if (config && config.dynamicRowDocuments) {
			return config.dynamicRowDocuments;
		}
	}

	@computed
	get dynamicRows(): any {
		const config = this.activeConfiguration;
		if (config && config.dynamicRows) {
			return this.getQueryValue(config.dynamicRows);
		}
	}

	async getAllChildRows(): Promise<any> {
		const { queryStore } = this.storeContext;
		const { props } = this.activeConfiguration;
		const childRows = [];
		if (props && props.grouping) {
			const rows = this.dynamicRows;
			if (rows) {
				for (const row of rows) {
					const { grouping } = props;
					let queryResponse = queryStore.query(grouping.getRowsQuery, { responseContext: { grouping, row } });
					if (grouping.orchestrateChildRows) {
						queryResponse = await grouping.orchestrateChildRows(queryResponse, grouping, row);
					}
					if (isArrayLike(queryResponse)) {
						childRows.push(...queryResponse);
					}
				}
			}
		}
		return childRows;
	}

	@action
	async initialize(): Promise<void> {
		const result = await this.getAllChildRows();
		runInAction(() => {
			this.childRows = result;
		});
		await this.fetchDocuments(this.dynamicRows);
		this.contextedTimestamp = new Date().getTime();

	}
}
